/**
 * Generic OIDC AuthProvider implementation.
 *
 * Uses `oidc-client-ts` UserManager for login redirect, token refresh,
 * and user-info retrieval.  Works with any OIDC-compliant IdP (Azure AD,
 * Okta, Keycloak, Auth0, etc.).
 */

import React, { useState, useEffect, useCallback, useMemo, useRef, ReactNode } from 'react';
import { UserManager, WebStorageStateStore, User as OidcUser } from 'oidc-client-ts';
import * as Sentry from '@sentry/react';
import { OIDC_AUTHORITY, OIDC_CLIENT_ID, OIDC_REDIRECT_URI, OIDC_AUDIENCE } from '../../config/auth';
import { useImpersonation } from './useImpersonation';
import { User, AuthContextType } from './types';
import { fetchDatabaseAdminFlag } from './backendAuthorization';
import PostHogService from '../../posthog';

/** Build shared UserManager settings, optionally including the API audience. */
function buildOidcSettings(overrides: Partial<ConstructorParameters<typeof UserManager>[0]> = {}) {
  return {
    authority: OIDC_AUTHORITY,
    client_id: OIDC_CLIENT_ID,
    redirect_uri: OIDC_REDIRECT_URI,
    response_type: 'code' as const,
    scope: 'openid profile email',
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    ...(OIDC_AUDIENCE ? { extraQueryParams: { audience: OIDC_AUDIENCE } } : {}),
    ...overrides,
  };
}

function buildUserManager(): UserManager {
  return new UserManager({
    ...buildOidcSettings(),
    post_logout_redirect_uri: window.location.origin,
    automaticSilentRenew: true,
  });
}

function mapOidcUser(oidcUser: OidcUser): User {
  const profile = oidcUser.profile;
  const email = profile.email || '';

  return {
    id: profile.sub,
    email,
    name: profile.name || '',
    username: profile.preferred_username || profile.nickname || email.split('@')[0],
    is_admin: false,
  };
}

interface Props {
  children: ReactNode;
  context: React.Context<AuthContextType | undefined>;
}

export const OIDCAuthProvider: React.FC<Props> = ({ children, context }) => {
  const managerRef = useRef<UserManager>(buildUserManager());
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Bootstrap: check for existing session or handle redirect callback
  useEffect(() => {
    const manager = managerRef.current;
    let isComponentMounted = true;

    const mapOidcUserWithDatabaseAuthorization = async (oidcUser: OidcUser): Promise<User> => {
      const mappedUser = mapOidcUser(oidcUser);
      const databaseAdminFlag = await fetchDatabaseAdminFlag(oidcUser.access_token);
      return {
        ...mappedUser,
        is_admin: databaseAdminFlag ?? false,
      };
    };

    const init = async () => {
      try {
        // Handle OIDC callback if present in the URL
        if (window.location.pathname === '/oidc/callback') {
          const oidcUser = await manager.signinRedirectCallback();
          const mappedUser = await mapOidcUserWithDatabaseAuthorization(oidcUser);
          if (isComponentMounted) {
            setUser(mappedUser);
          }
          window.history.replaceState({}, '', '/repositories');
          if (isComponentMounted) {
            setIsLoading(false);
          }
          return;
        }

        // Try to load existing session
        const oidcUser = await manager.getUser();
        if (oidcUser && !oidcUser.expired) {
          const mappedUser = await mapOidcUserWithDatabaseAuthorization(oidcUser);
          if (isComponentMounted) {
            setUser(mappedUser);
          }
        }
      } catch (err) {
        console.error('OIDC init error:', err);
      } finally {
        if (isComponentMounted) {
          setIsLoading(false);
        }
      }
    };

    void init();

    // Listen for token refresh events
    const onUserLoaded = (oidcUser: OidcUser) => {
      void mapOidcUserWithDatabaseAuthorization(oidcUser).then((mappedUser) => {
        if (isComponentMounted) {
          setUser(mappedUser);
        }
      });
    };
    const onUserUnloaded = () => setUser(null);

    manager.events.addUserLoaded(onUserLoaded);
    manager.events.addUserUnloaded(onUserUnloaded);

    return () => {
      isComponentMounted = false;
      manager.events.removeUserLoaded(onUserLoaded);
      manager.events.removeUserUnloaded(onUserUnloaded);
    };
  }, []);

  const isAdmin = user?.is_admin || false;
  const {
    impersonationContext,
    startImpersonation,
    stopImpersonation,
    getImpersonationTimeRemaining,
  } = useImpersonation(user, isAdmin);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    const oidcUser = await managerRef.current.getUser();
    return oidcUser?.access_token || null;
  }, []);

  const logout = useCallback(async () => {
    await managerRef.current.signoutRedirect();
    setUser(null);
    Sentry.setUser(null);
    PostHogService.reset();
  }, []);

  const checkAuth = useCallback(async () => user !== null, [user]);

  const isAuthenticated = user !== null;
  const displayName = user?.name || user?.username || user?.email || 'User';

  useEffect(() => {
    if (user) Sentry.setUser({ id: user.id, email: user.email });
  }, [user]);

  const value = useMemo<AuthContextType>(
    () => ({
      user,
      isAuthenticated,
      isLoading,
      displayName,
      logout,
      checkAuth,
      getAccessToken,
      isAdmin,
      impersonationContext,
      startImpersonation,
      stopImpersonation,
      getImpersonationTimeRemaining,
    }),
    [
      user, isAuthenticated, isLoading, displayName,
      logout, checkAuth, getAccessToken, isAdmin,
      impersonationContext, startImpersonation,
      stopImpersonation, getImpersonationTimeRemaining,
    ],
  );

  return <context.Provider value={value}>{children}</context.Provider>;
};

/**
 * Trigger the OIDC login redirect.
 * Call this from a "Sign in with SSO" button.
 */
export function oidcSignIn(): void {
  new UserManager(buildOidcSettings()).signinRedirect();
}
